import { BrowserRouter as Router, Link, useNavigate } from 'react-router-dom';
import { Suspense, useState, useEffect } from 'react';
import { DarkModeToggle } from './components/dark-mode-toggle';
import { FeedbackButton } from './components/feedback-button';
import { SyfoAdviserIcon } from './components/icons/syfo-adviser-icon';
import { SyfoText } from './components/icons/syfo-text';
import { AdviserRoutes } from './routes';
import { AppErrorBoundary } from './components/error-boundaries';
import { useAppDispatch, useAppSelector } from './stores/hooks';
import { checkAuthThunk } from './stores/thunks/check-auth-thunk';
import './app.css';

function App() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      await dispatch(checkAuthThunk()).unwrap();
      setIsLoadingAuth(false);
    };
    checkAuth();
  }, [dispatch]);
  
  const handleNavigateHome = () => {
    navigate('/');
  };

  // Show loading spinner while checking authentication
  if (isLoadingAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show header only when authenticated
  const showHeader = isAuthenticated;

  return (
    <AppErrorBoundary onNavigateHome={handleNavigateHome}>
      <div className="flex flex-col min-h-screen">
        {/* Global App Header - only show when authenticated */}
        {showHeader && (
          <header className="sticky top-0 z-50 bg-background flex items-center justify-between py-2 px-4 border-b">
            <div className="flex items-center gap-2">
              <Link className="flex items-center" to="/">
                <SyfoAdviserIcon className="h-9 w-9 pt-1 pr-1" />
                <SyfoText className="h-10 w-auto" />
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <FeedbackButton />
              <DarkModeToggle />
            </div>
          </header>
        )}

        {/* Page Content */}
        <div className="flex-1 overflow-auto">
          <Suspense fallback={
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading...</p>
              </div>
            </div>
          }>
            <AdviserRoutes />
          </Suspense>
        </div>
      </div>
    </AppErrorBoundary>
  );
}

// Wrapper component to provide Router context
function AppWithRouter() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWithRouter;